/**
 * PM Training Clip 2 — Setting Up Your Event
 * Scene 1: CompetitionView — click Setup card.
 * Scene 2: Setup page — section-by-section walkthrough with highlights.
 * 750 frames at 30fps = 25 seconds.
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate } from "remotion";
import { AutoCursor } from "../components/AutoCursor";
import { ScreenMock } from "../components/ScreenMock";
import { FadeTransition } from "../components/FadeTransition";
import { StepLabel } from "../components/StepLabel";
import { Button } from "../../components/ui/Button";

const ACCENT = "#a855f7";
const BLUE = "#3b82f6";

export function PMCourse2() {
  return (
    <AbsoluteFill style={{ backgroundColor: "#0a0a0b", fontFamily: "'Inter', sans-serif" }}>
      <Sequence durationInFrames={55}><Title /></Sequence>
      <Sequence from={45} durationInFrames={120}><SceneCompView /></Sequence>
      <Sequence from={155} durationInFrames={540}>
        <FadeTransition durationInFrames={540} fadeIn={10} fadeOut={10}><SceneSetup /></FadeTransition>
      </Sequence>
      <Sequence from={690} durationInFrames={60}><Done /></Sequence>
    </AbsoluteFill>
  );
}

function Title() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12, 40, 55], [0, 1, 1, 0], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ fontSize: 10, color: ACCENT, textTransform: "uppercase", letterSpacing: 3, fontFamily: M, marginBottom: 14 }}>Competition Manager Training · Clip 2</div>
      <div style={{ fontSize: 52, fontWeight: 800, color: "#fff" }}>Setting Up Your Event</div>
    </AbsoluteFill>
  );
}

/* ── Scene 1: CompetitionView — click Setup card ── */

function SceneCompView() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 10, 100, 120], [0, 1, 1, 0], { extrapolateRight: "clamp" });

  return (
    <AbsoluteFill style={{ opacity: op }}>
      <ScreenMock url="raley.koda-software.com/competition/boardstock">
        <div style={shell}>
          <div style={topbar}>
            <span style={brand}>RALEY</span>
            <span style={{ ...navLink, color: "#e4e4e7" }}>COMPETITIONS</span>
            <span style={navLink}>RIDERS</span>
            <span style={navLink}>TRAINING</span>
            <span style={{ marginLeft: "auto", fontSize: 11, color: "#71717a", ...mono }}>Takoda Mundy</span>
          </div>
          <div style={{ padding: "16px 24px" }}>
            {/* Header */}
            <div className="page-header">
              <div>
                <h1 className="page-title" style={{ margin: 0 }}>BOARDSTOCK</h1>
                <div className="flex gap-3 items-center" style={{ marginTop: 4 }}>
                  <span className="text-sm text-muted">Competition</span>
                  <span className="badge badge-live">LIVE</span>
                </div>
                <div className="flex gap-2" style={{ marginTop: 6 }}>
                  <span className="badge" style={{ background: BLUE, color: "#fff", fontSize: 11 }}>Admin</span>
                </div>
              </div>
            </div>

            {/* Cards grid */}
            <div className="grid-3">
              <div id="setup-card" className="card" style={{ cursor: "pointer" }}>
                <div className="card-title">Setup</div>
                <p className="text-sm text-muted" style={{ marginTop: 8 }}>
                  Configure divisions, heats, assign riders, and invite your team.
                </p>
                <div style={{ marginTop: 12 }}>
                  <span className="badge" style={{ background: BLUE, color: "#fff" }}>Admin</span>
                </div>
              </div>
              <div className="card">
                <div className="card-title">Judge</div>
                <p className="text-sm text-muted" style={{ marginTop: 8 }}>Score riders in real-time with 0.1 precision.</p>
                <div style={{ marginTop: 12 }}>
                  <span className="badge" style={{ background: "#22c55e", color: "#fff" }}>Access</span>
                </div>
              </div>
              <div className="card">
                <div className="card-title">Producer</div>
                <p className="text-sm text-muted" style={{ marginTop: 8 }}>Control the live overlay. Focus riders, show scores, leaderboard.</p>
                <div style={{ marginTop: 12 }}>
                  <span className="badge" style={{ background: ACCENT, color: "#fff" }}>Access</span>
                </div>
              </div>
            </div>

            {/* Team table */}
            <div style={{ marginTop: 32 }}>
              <div className="card-title" style={{ marginBottom: 12 }}>Team (2)</div>
              <table className="table">
                <thead><tr><th>Name</th><th>Email</th><th>Roles</th><th>Judge #</th></tr></thead>
                <tbody>
                  <tr>
                    <td>Takoda Mundy</td>
                    <td className="text-mono text-sm">takamundy@gmail.com</td>
                    <td><span className="badge" style={{ background: BLUE, color: "#fff", fontSize: 11 }}>Admin</span></td>
                    <td>-</td>
                  </tr>
                  <tr>
                    <td>Britta Madsen</td>
                    <td className="text-mono text-sm">britta.a.madsen@gmail.com</td>
                    <td><span className="badge" style={{ background: "#22c55e", color: "#fff", fontSize: 11 }}>Judge</span></td>
                    <td>1</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <AutoCursor points={[
          { x: 960, y: 400, frame: 0 },
          { target: "setup-card", frame: 40 },
          { target: "setup-card", frame: 70, click: true },
          { x: 960, y: 400, frame: 110 },
        ]} />
      </ScreenMock>
      <StepLabel text="Click Setup to configure your competition" show={5} hide={90} color={ACCENT} />
    </AbsoluteFill>
  );
}

/* ── Scene 2: Setup page — section walkthrough ── */

function SceneSetup() {
  const frame = useCurrentFrame();

  /*
   * Sections highlighted with glow:
   *   0-120:   Divisions (left top)
   *   120-250: Heats (left bottom)
   *   250-360: Competition Settings (right top)
   *   360-470: Invite Team (right bottom)
   *   470-530: Full view
   */
  const section =
    frame < 120 ? "divisions" :
    frame < 250 ? "heats" :
    frame < 360 ? "settings" :
    frame < 470 ? "invite" :
    "none";

  return (
    <AbsoluteFill>
      <ScreenMock url="raley.koda-software.com/competition/boardstock/setup">
        <div style={shell}>
          <div style={topbar}>
            <span style={brand}>RALEY</span>
            <span style={{ ...navLink, color: "#e4e4e7" }}>COMPETITIONS</span>
            <span style={navLink}>RIDERS</span>
            <span style={navLink}>TRAINING</span>
            <span style={{ marginLeft: "auto", fontSize: 11, color: "#71717a", ...mono }}>Takoda Mundy</span>
          </div>
          <div style={{ padding: "12px 24px", overflow: "hidden" }}>
            {/* Header */}
            <div style={{ borderBottom: "1px solid var(--c-border, #2a2a32)", paddingBottom: 10, marginBottom: 14 }}>
              <h1 className="page-title" style={{ margin: 0 }}>SETUP: BOARDSTOCK</h1>
              <p className="text-sm text-muted" style={{ marginTop: 4 }}>Configure divisions, heats, assign riders, invite judges</p>
            </div>

            {/* Two-column layout */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              {/* Left column */}
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {/* Divisions */}
                <div id="sec-divisions" className="card" style={{ ...glow(section === "divisions") }}>
                  <div className="card-header"><div className="card-title">Divisions</div></div>
                  <div className="flex gap-2" style={{ marginBottom: 12 }}>
                    <input className="input" placeholder='Division name (e.g. Men&apos;s Open)' readOnly style={{ flex: 1, background: "var(--c-surface-2)", border: "1px solid var(--c-border)", padding: "8px 10px", color: "var(--c-text-muted)", fontSize: 12 }} />
                    <Button>Add</Button>
                  </div>
                  {["Men's", "Women's"].map((d) => (
                    <div key={d} className="flex items-center justify-between" style={{ padding: "6px 0", borderBottom: "1px solid var(--c-border)" }}>
                      <span style={{ fontWeight: 600 }}>{d}</span>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm">Edit</Button>
                        <Button variant="danger" size="sm">Delete</Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Heats */}
                <div id="sec-heats" className="card" style={{ ...glow(section === "heats") }}>
                  <div className="card-header"><div className="card-title">Heats</div></div>
                  <div className="flex gap-2" style={{ marginBottom: 8 }}>
                    <input className="input" placeholder="Heat name..." readOnly style={{ flex: 1, background: "var(--c-surface-2)", border: "1px solid var(--c-border)", padding: "8px 10px", color: "var(--c-text-muted)", fontSize: 12 }} />
                    <Button>Add</Button>
                  </div>
                  <div className="flex gap-3" style={{ marginTop: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div className="field-label" style={{ fontSize: 10, marginBottom: 4 }}>Division</div>
                      <div style={{ background: "var(--c-surface-2)", border: "1px solid var(--c-border)", padding: "6px 8px", fontSize: 12, color: "var(--c-text-muted)", display: "flex", justifyContent: "space-between" }}>
                        <span>Select a division...</span>
                        <span style={{ fontSize: 8 }}>▾</span>
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="field-label" style={{ fontSize: 10, marginBottom: 4 }}>Max Attempts</div>
                      <div className="flex items-center gap-2">
                        <div style={{ flex: 1, height: 4, background: "var(--c-border)", position: "relative" }}>
                          <div style={{ position: "absolute", left: 0, top: 0, height: 4, width: "0%", background: BLUE }} />
                          <div style={{ position: "absolute", left: "0%", top: -4, width: 12, height: 12, borderRadius: "50%", background: BLUE }} />
                        </div>
                        <span className="text-mono" style={{ fontSize: 12 }}>1</span>
                      </div>
                    </div>
                  </div>

                  {/* Existing heats grouped by division */}
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--c-text-muted)", marginBottom: 4, fontWeight: 600 }}>Men&apos;s</div>
                    {[{ name: "Finals", att: 4 }, { name: "Heat 1", att: 3 }].map((h) => (
                      <div key={h.name} style={{ background: "var(--c-bg)", border: "1px solid var(--c-border)", padding: "8px 12px", marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 12 }}>▶</span>
                          <span style={{ fontWeight: 700, fontSize: 12 }}>{h.name}</span>
                        </div>
                        <div className="flex gap-2">
                          <span className="badge badge-off">Max: 10</span>
                          <span className="badge badge-off">{h.att} attempts</span>
                          <Button variant="danger" size="sm">Delete</Button>
                        </div>
                      </div>
                    ))}
                    <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--c-text-muted)", marginTop: 10, marginBottom: 4, fontWeight: 600 }}>Women&apos;s</div>
                    <div style={{ background: "var(--c-bg)", border: "1px solid var(--c-border)", padding: "8px 12px", marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 12 }}>▶</span>
                        <span style={{ fontWeight: 700, fontSize: 12 }}>Finals</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="badge badge-off">Max: 10</span>
                        <span className="badge badge-off">3 attempts</span>
                        <Button variant="danger" size="sm">Delete</Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right column */}
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {/* Competition Settings */}
                <div id="sec-settings" className="card" style={{ ...glow(section === "settings") }}>
                  <div className="card-header"><div className="card-title">Competition Settings</div></div>
                  <div className="field" style={{ marginBottom: 12 }}>
                    <label className="field-label">Scoring Model</label>
                    <div style={{ background: "var(--c-surface-2)", border: "1px solid var(--c-border)", padding: "8px 10px", fontSize: 13, color: "var(--c-text)", display: "flex", justifyContent: "space-between" }}>
                      <span>Best Score (Max)</span>
                      <span style={{ fontSize: 10, color: "var(--c-text-muted)" }}>▾</span>
                    </div>
                    <div className="text-sm text-muted" style={{ marginTop: 6 }}>Rider keeps only their highest judge score</div>
                  </div>
                  <div className="text-sm text-muted" style={{ marginBottom: 16 }}>Judges: 3 (set at creation, cannot be changed)</div>

                  <div className="field-label" style={{ marginBottom: 6 }}>Overlay Defaults (applied on Go Live)</div>
                  {[
                    { label: "Show heat name banner", checked: true },
                    { label: "Show heat riders", checked: true },
                    { label: "Show rider scores", checked: false },
                    { label: "Show leaderboard", checked: false },
                  ].map((cb) => (
                    <label key={cb.label} className="flex items-center gap-2" style={{ fontSize: 12, marginBottom: 6, cursor: "pointer" }}>
                      <div style={{ width: 14, height: 14, border: `1px solid ${cb.checked ? BLUE : "var(--c-border)"}`, background: cb.checked ? BLUE : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {cb.checked && <span style={{ fontSize: 9, color: "#fff" }}>✓</span>}
                      </div>
                      {cb.label}
                    </label>
                  ))}

                  <div className="field-label" style={{ marginTop: 12, marginBottom: 6 }}>Default Overlay Style</div>
                  <div style={{ background: "var(--c-surface-2)", border: "1px solid var(--c-border)", padding: "8px 10px", fontSize: 13, color: "var(--c-text)", display: "flex", justifyContent: "space-between" }}>
                    <span>Steel</span>
                    <span style={{ fontSize: 10, color: "var(--c-text-muted)" }}>▾</span>
                  </div>
                </div>

                {/* Invite Team */}
                <div id="sec-invite" className="card" style={{ ...glow(section === "invite") }}>
                  <div className="card-header"><div className="card-title">Invite Team</div></div>
                  <div className="flex gap-2" style={{ marginBottom: 8 }}>
                    <input className="input" placeholder="Email address" readOnly style={{ flex: 1, background: "var(--c-surface-2)", border: "1px solid var(--c-border)", padding: "8px 10px", color: "var(--c-text-muted)", fontSize: 12 }} />
                    <Button>Invite</Button>
                  </div>
                  <div className="flex gap-3" style={{ marginBottom: 12 }}>
                    {[
                      { role: "Judge", color: "#22c55e", checked: true },
                      { role: "Producer", color: ACCENT, checked: false },
                      { role: "Admin", color: BLUE, checked: false },
                    ].map((r) => (
                      <label key={r.role} className="flex items-center gap-2" style={{ fontSize: 12, cursor: "pointer" }}>
                        <div style={{ width: 12, height: 12, border: `1px solid ${r.color}`, background: r.checked ? r.color : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {r.checked && <span style={{ fontSize: 8, color: "#fff" }}>✓</span>}
                        </div>
                        {r.role}
                      </label>
                    ))}
                  </div>
                  <div className="flex items-center justify-between text-sm" style={{ padding: "4px 0" }}>
                    <span className="text-mono">britta.a.madsen@gmail.com</span>
                    <span className="badge" style={{ background: "#22c55e", color: "#fff", fontSize: 11 }}>Judge</span>
                    <span style={{ color: "var(--c-text-muted)" }}>Accepted</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <AutoCursor points={[
          { x: 960, y: 300, frame: 0 },
          // Divisions
          { target: "sec-divisions", frame: 30 },
          { target: "sec-divisions", frame: 110 },
          // Heats
          { target: "sec-heats", frame: 130 },
          { target: "sec-heats", frame: 240 },
          // Settings
          { target: "sec-settings", frame: 260 },
          { target: "sec-settings", frame: 350 },
          // Invite
          { target: "sec-invite", frame: 370 },
          { target: "sec-invite", frame: 460 },
          // Full view
          { x: 960, y: 400, frame: 475 },
          { x: 960, y: 400, frame: 530 },
        ]} />
      </ScreenMock>

      <StepLabel text="Divisions group your heats — e.g. Men's Open, Women's Open" show={5} hide={115} color={ACCENT} />
      <StepLabel text="Heats are rounds within a division — set name, division, and max attempts" show={120} hide={245} color={BLUE} />
      <StepLabel text="Competition Settings — scoring model, judge count, and overlay defaults for Go Live" show={250} hide={355} color="#eab308" />
      <StepLabel text="Invite Team — add judges, producers, or admins by email" show={360} hide={465} color="#22c55e" />
      <StepLabel text="That's Setup — divisions, heats, settings, and team all in one place" show={470} hide={530} color="#22c55e" />
    </AbsoluteFill>
  );
}

function Done() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12], [0, 1], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#22c55e", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
        <span style={{ fontSize: 24, color: "#fff" }}>✓</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: "#fff" }}>Your event is ready to run</div>
    </AbsoluteFill>
  );
}

/* ── Helpers ── */

function glow(active: boolean): React.CSSProperties {
  return active
    ? { boxShadow: `0 0 0 2px ${BLUE}, 0 0 15px rgba(59,130,246,0.25)`, borderColor: BLUE }
    : {};
}

/* ── Styles ── */
const shell: React.CSSProperties = { width: "100%", height: "100%", background: "var(--c-bg, #0a0a0b)", fontFamily: "'Inter', sans-serif", color: "var(--c-text, #e4e4e7)", display: "flex", flexDirection: "column" };
const topbar: React.CSSProperties = { height: 44, background: "var(--c-surface, #111113)", borderBottom: "1px solid var(--c-border, #2a2a32)", display: "flex", alignItems: "center", gap: 24, padding: "0 24px", flexShrink: 0 };
const brand: React.CSSProperties = { fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, fontSize: 14, letterSpacing: 2 };
const mono: React.CSSProperties = { fontFamily: "'JetBrains Mono', monospace" };
const M = "'JetBrains Mono', monospace";
const navLink: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--c-text-muted, #71717a)" };
